"use client";

import { useMemo } from "react";
import dynamic from "next/dynamic";
import { Box, Card, CardContent, Divider, Typography } from "@mui/material";
import type { EChartsOption } from "echarts";
import type { FlowRow } from "../types";
import { formatBillions } from "../utils";

const ReactECharts = dynamic(() => import("echarts-for-react"), { ssr: false });

type CreditFlowsSankeyProps = {
  rows: FlowRow[];
  noCard?: boolean;
};

export default function CreditFlowsSankey({
  rows,
  noCard = false,
}: CreditFlowsSankeyProps) {
  const sankeyOption = useMemo(() => {
    const NODE_ABBREVIATIONS: Record<string, string> = {
      "China, Multilateral; Regional; or Plurilateral Lenders":
        "China, Multilateral",
      "Private Creditor(s), Multilateral; Regional; or Plurilateral Lenders":
        "Private Creditor(s), Multilateral",
    };

    const formatNodeName = (name: string) => {
      const stripped = name
        .replace(/^(CR|CC|BR|BC):\s*/, "")
        .replace(/\bBorrower\s+/g, "")
        .replace(/\bCreditor\s+/g, "")
        .replace(/\bGeographic\s+/g, "")
        .trim();
      return NODE_ABBREVIATIONS[stripped] ?? stripped;
    };

    const nodeSet = new Set<string>();
    const nodeInTotals = new Map<string, number>();
    const nodeOutTotals = new Map<string, number>();

    rows.forEach((row) => {
      const amount = Number(row["SUM(TotalCommitmentAmount)"] ?? 0);
      nodeSet.add(row.source);
      nodeSet.add(row.target);

      nodeOutTotals.set(
        row.source,
        (nodeOutTotals.get(row.source) ?? 0) + amount,
      );
      nodeInTotals.set(
        row.target,
        (nodeInTotals.get(row.target) ?? 0) + amount,
      );
    });

    const getFlowTooltipData = (params: unknown) => {
      const point = Array.isArray(params) ? params[0] : params;
      const data = (point as { data?: unknown } | undefined)?.data as
        | { value?: number; source?: string; target?: string }
        | undefined;

      return {
        value: data?.value ?? 0,
        source: data?.source,
        target: data?.target,
      };
    };

    return {
      tooltip: {
        trigger: "item",
        formatter: (params) => {
          const point = Array.isArray(params) ? params[0] : params;
          const typedPoint = point as {
            dataType?: string;
            name?: string;
            data?: { name?: string };
          };

          if (typedPoint.dataType === "node") {
            const rawNodeName = typedPoint.name ?? typedPoint.data?.name ?? "";
            const incoming = nodeInTotals.get(rawNodeName) ?? 0;
            const outgoing = nodeOutTotals.get(rawNodeName) ?? 0;
            const total = Math.max(incoming, outgoing);

            return `${formatNodeName(rawNodeName)}<br/>$${formatBillions(total)}`;
          }

          const { value, source, target } = getFlowTooltipData(params);
          if (source && target) {
            return `${formatNodeName(source)} -> ${formatNodeName(target)}<br/>$${formatBillions(value)}`;
          }
          return `$${formatBillions(value)}`;
        },
      },
      series: [
        {
          type: "sankey",
          left: "2%",
          right: "8%",
          top: "4%",
          bottom: "4%",
          data: Array.from(nodeSet).map((name) => ({ name })),
          links: rows.map((row) => ({
            source: row.source,
            target: row.target,
            value: row["SUM(TotalCommitmentAmount)"] ?? 0,
          })),
          nodeWidth: 14,
          nodeGap: 16,
          lineStyle: {
            color: "source",
            opacity: 0.3,
            curveness: 0.5,
          },
          label: {
            color: "#2f3e4e",
            fontSize: 11,
            fontFamily: "Inter",
            fontWeight: 500,
            formatter: ({ name }: { name: string }) => formatNodeName(name),

            // text stroke
            textBorderColor: "#ffffff",
            textBorderWidth: 1,
          },
          labelLayout: { moveOverlap: "shiftY" },
          emphasis: {
            focus: "adjacency",
          },
        },
      ],
    } as EChartsOption;
  }, [rows]);

  const inner = (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          mb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#363636" }}>
          Credit Flows
        </Typography>
        <Box
          component="img"
          src="/SDF-Logo.png"
          alt="Sovereign Debt Forum logo"
          sx={{
            width: { xs: 92, sm: 150 },
            height: "auto",
            flexShrink: 0,
            opacity: 0.7,
          }}
        />
      </Box>
      <Divider sx={{ mb: 4 }} />
      <ReactECharts
        option={sankeyOption}
        style={{ height: 540, width: "100%" }}
        notMerge
      />
      <Typography
        sx={{
          mt: 1,
          fontSize: "0.8rem",
          color: "#111827",
          textAlign: "center",
        }}
      >
        This data reflects the documents available on our platform, not all
        sovereign debt.
      </Typography>
    </>
  );

  if (noCard) {
    return <Box sx={{ mt: 3 }}>{inner}</Box>;
  }

  return (
    <Card sx={{ mx: 0, px: 0, pt: 0 }}>
      <CardContent sx={{ px: 1, py: 2 }}>{inner}</CardContent>
    </Card>
  );
}
